import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useLanguage } from '@/context/LanguageContext';
import LanguageSelector from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardFooter } from '@/components/ui/card';
import { ShieldCheck, Lock, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const { verifyCode, setUserPhone } = useApp();
  const { t } = useLanguage();
  const [code, setCode] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const user = await verifyCode(code);
      if (user) {
        setUserPhone(phone);
        toast.success(t('loginSuccess'));
        navigate('/dashboard');
      } else {
        setError(t('invalidCode'));
        toast.error(t('invalidCodeToast'));
      }
    } catch (err) {
      setError(t('connectionError'));
      toast.error(t('connectionErrorToast'));
    }
    setIsLoading(false);
  };

  const handleForgotCode = () => {
    toast.info(t('contactSupport'));
  };

  return (
    <div className="min-h-screen flex flex-col vault-bg">
      <div className="header-nav px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo.png" alt="BMPay" className="h-8 w-8" />
            <span className="font-bold text-[#0A2540] text-lg font-['Manrope'] tracking-tight">BMPay</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <div className="hidden sm:flex items-center gap-2 text-slate-500 text-sm">
              <ShieldCheck className="h-4 w-4 text-emerald-600" />
              <span>{t('secureSpace')}</span>
            </div>
          </div>
        </div>
      </div>

      <main className="flex-1 flex items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-md animate-slide-up">
          <div className="text-center mb-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2540] font-['Manrope'] tracking-tight mb-2" data-testid="main-title">
              {t('clientSpace')}
            </h1>
            <p className="text-slate-600 text-sm sm:text-base" data-testid="welcome-message">
              {t('verifyAndTransfer')}
            </p>
          </div>

          <Card className="secure-card-navy rounded-xl overflow-hidden">
            <CardHeader className="bg-gradient-to-b from-slate-50 to-white pb-4 pt-6">
              <h2 className="text-lg font-semibold text-center text-[#0A2540] font-['Manrope']" data-testid="login-title">
                {t('loginToAccount')}
              </h2>
            </CardHeader>

            <CardContent className="px-5 pb-5">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1">
                  <Label htmlFor="code" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    {t('identificationCode')}
                  </Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder={t('enterCode')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="h-10 text-sm border-slate-300 bg-white focus:border-[#0A2540] focus:ring-[#0A2540] rounded-lg px-3"
                    data-testid="code-input"
                    required
                  />
                  <p className="text-xs text-slate-500">{t('codeProvidedByBMPay')}</p>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="phone" className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                    <Lock className="h-3 w-3" />
                    {t('phoneNumber')}
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder={t('phonePlaceholder')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-10 text-sm border-slate-300 bg-white focus:border-[#0A2540] focus:ring-[#0A2540] rounded-lg px-3"
                    data-testid="phone-input"
                    required
                  />
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="error-message">
                    <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-700">{error}</p>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-11 bg-[#0A2540] hover:bg-[#163454] text-white font-semibold text-sm rounded-lg transition-all duration-200"
                  disabled={isLoading || !code || !phone}
                  data-testid="login-button"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner border-white border-t-transparent"></span>
                      {t('connecting')}
                    </span>
                  ) : (
                    t('accessMyAccount')
                  )}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleForgotCode}
                  className="text-xs text-[#0A2540] hover:text-[#163454] hover:underline font-medium transition-colors"
                  data-testid="forgot-code-button"
                >
                  {t('forgotCode')}
                </button>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col items-center py-4 bg-slate-50 border-t border-slate-100">
              <div className="security-badge text-xs">
                <ShieldCheck className="h-3 w-3" />
                <span>{t('sslSecure')}</span>
              </div>
            </CardFooter>
          </Card>

          <footer className="mt-6 text-center">
            <p className="text-xs text-slate-400">&copy; {t('copyright')}</p>
          </footer>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
